import { Grid } from '@mui/material';
import { isArray } from 'lodash';
import React, { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useRecoilValue } from 'recoil';
import { VerificationAgencyClientApplicationService } from '../../../Component/Api/VerificationAgency/VerificationAgencyClientApplication/VerificationAgencyClientApplicationService';
import { MDBox } from '../../../Component/Common/Components/MDBox';
import { MDTypography } from '../../../Component/Common/Components/MDTypography';
import { ISteps, StatusStepper } from '../../../Component/Common/Components/Stepper/Stepper';
import { ApplicationStatusOptionsDto, TrackClientStatusDtoIn, VerificationAgencyClientApplication } from '../../../Component/Model/Dto/VerificationAgency/VerificationAgencyClientApplication';
import { defaultObject } from '../../../Helpers/typescriptHelper';
import { VerificationCompanyAtom } from '../../../State/VerificationCompany.Atom';
import { VerificationClientStatus, vaClientStatusLookupName } from '../../Common/StaticData/VAClientStatus';
import { StatusCard } from './StatusCard';
import { FieldValues } from 'react-hook-form';
import { StatusDialog } from './StatusDialog';

interface VerificationStatus {
    trackClientStatusId: number;
    clientStatusId: number,
    completed: boolean,
    dateAchieved?: Date,
    description: any,
    applicationTrackingElementStatus: number;
}

export const Status = () => {
    const verificationCompany = useRecoilValue(VerificationCompanyAtom);
    const [riskAssessment, setRiskAssessment] = React.useState<VerificationAgencyClientApplication.RiskAssessmentDto>();
    const [steps, setSteps] = React.useState<ISteps[]>([]);
    const [showModal, setShowModal] = React.useState(false);
    const [selectedStep, setSelectedStep] = React.useState(defaultObject);
    const [application, setApplication] = React.useState<VerificationAgencyClientApplication.VerificationAgencyClientApplicationResponse>();
    const [options, setOptions] = React.useState<ApplicationStatusOptionsDto>();

    const onStepCick = (id: number) => {
        const updatedItem = steps.find(item => item.id === id)!;
        setSelectedStep(updatedItem);
        setShowModal(true);
    }

    const onCloseModal = () => {
        setShowModal(false);
        fetchVAClientStatus();
    }

    const fetchDropdowns = async () => {
        let response = await VerificationAgencyClientApplicationService.GetDropdownOptions();
        setOptions(response.data);
    }

    useEffect(() => {
        fetchDropdowns();
    }, []);

    const updateStatus = async (data: FieldValues) => {
        try {
            const model: TrackClientStatusDtoIn = {
                ClientStatus: data.clientStatus,
                DateAchieved: new Date(data.date) ?? new Date(),
                ApplicationId: verificationCompany.applicationId,
                Description: data.description,
                VaClientId: verificationCompany.vaClientId,
                ApplicationTrackingElementStatus: parseInt(data.status),
                InvoicePaymentStatus: data.invoicePaymentStatus
            }

            await VerificationAgencyClientApplicationService.ManuallyCaptureClientStatus(model);

            onCloseModal();
            toast.success('Successfully Saved Status');
        }
        catch (error: any) {
            if (isArray(error)) {
                let Errors: string[] = error;
                Errors.forEach((error: string) => {
                    toast.error(error);
                });
            }
        }
    }

    const fetchRiskAssessmentData = async () => {
        try {
            const applicationId = verificationCompany.applicationId;
            let response = await VerificationAgencyClientApplicationService.GetRiskAssessmentByApplicationId(applicationId);
            setRiskAssessment(response.data)
        } catch (error) {
            console.log(error);
        }
    }

    const fetchVAClientStatus = async () => {
        try {
            let clientStatusList: VerificationStatus[];
            const clientStatus = await VerificationAgencyClientApplicationService.GetClientStatus(verificationCompany.applicationId);

            await fetchRiskAssessmentData();
            clientStatusList = clientStatus.data.map((item) => {
                return {
                    trackClientStatusId: item.trackClientStatusId, 
                    clientStatusId: item.clientStatus, 
                    completed: item.applicationTrackingElementStatus >= 2 || item.applicationTrackingElementStatus === 0, 
                    dateAchieved: item.dateAchieved, 
                    description: item.description,
                    applicationTrackingElementStatus: item.applicationTrackingElementStatus
                }
            });

            const stepsData: ISteps[] = clientStatusList.map(item => {
                return {
                    recordId: item.trackClientStatusId, 
                    id: item.clientStatusId, 
                    label: vaClientStatusLookupName(item.clientStatusId), 
                    completed: item.completed, 
                    dateAchieved: item.dateAchieved, 
                    description: item.description,
                    state: item.applicationTrackingElementStatus,  
                }
            });
            setSteps(stepsData);
        } catch {
        }
    }

    const fetchApplication = async () => {
        try {
            const response = await VerificationAgencyClientApplicationService.GetById(verificationCompany.applicationId);
            setApplication(response.data);
        } catch (error) {
            console.log(error);
        }
    }

    const RiskAssessmentFields = (): { text: string; value: boolean }[] => {
        return [
            { text: "Sufficient capacity has been confirmed", value: !!riskAssessment?.confirmedSufficientCapacity },
            { text: "The Technical Signatory is impartial", value: !!riskAssessment?.isTheTechnicalSignatoryImpartial },
            { text: "The allocated Analyst(s) are impartial", value: !!riskAssessment?.isTheAllocatedAnalystsImpartial },
            { text: "There are related parties", value: !!riskAssessment?.areThereRelatedParties },
            { text: "Sufficient information was supplied", value: !!riskAssessment?.sufficientInformationWasSupplied },
            { text: "Verification requirements were provided", value: !!riskAssessment?.verificationRequirementsProvided },
            { text: "There are no differences in understanding", value: !!riskAssessment?.confirmedNoDifferencesInUnderstanding }
        ];
    };

    const GetStatusLink = (stepId: number) => {
        if (stepId == VerificationClientStatus.Verification_Agreement){
            return 'VerificationAgreement';
        }
        
        if (stepId == VerificationClientStatus.Invoice){
            return 'Invoice';
        }
        return '';
    }

    const RenderAccordionColumns = (steps: ISteps[], numberOfColumns: number) => {
        const arrayLength = steps.length;
        const columns: ISteps[][] = [];
        for (let i = 0; i < numberOfColumns; i++) {
            columns.push([]);
        }

        for (let i = 0; i < arrayLength; i++) {
            const columnIndex = i % numberOfColumns;
            columns[columnIndex].push(steps[i]);
        }

        const riskAssessmentFlags = RiskAssessmentFields();
        return (
            <Grid container spacing={2} mt={1} xs={12}>
                {columns.map((column, columnIndex) => {
                    return (
                        <Grid item xs={4} key={`column-${columnIndex}`} >
                            {column.map((step, stepIndex) => {
                                return (
                                    <StatusCard key={`step-${stepIndex}`}
                                        step={step}
                                        riskAssessmentFlags={riskAssessmentFlags}
                                        riskAssessmentNotes={riskAssessment?.additionalText}
                                        link={GetStatusLink(step.id)}
                                        updateStatus={updateStatus}
                                        options={options} />
                                );
                            })}
                        </Grid>
                    );
                })}
            </Grid>
        );
    };

    useEffect(() => {
        if (verificationCompany.applicationId !== -1) {
            fetchApplication();
            fetchVAClientStatus();
        }
    }, [verificationCompany.applicationId]);

    return (
        <>
            <MDBox style={{ marginBottom: "10px" }}>
                <MDTypography variant='h4' fontWeight='bold'>
                    Application - {application?.referenceNumber}
                </MDTypography>
            </MDBox>

            <StatusStepper steps={steps} onStepClick={onStepCick} />

            <StatusDialog
                showModal={showModal}
                onCloseModal={onCloseModal}
                selectedStep={selectedStep}
                updateStatus={updateStatus}
                link={GetStatusLink(selectedStep.id)}
                riskAssessmentFlags={RiskAssessmentFields()}
                riskAssessmentNotes={riskAssessment?.additionalText} />

            {RenderAccordionColumns(steps, 3)}

        </>
    )
}
